import { useCallback, useEffect, useState } from 'react'

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { IoHandLeftOutline } from 'react-icons/io5'
import { MdDeleteForever, MdDragIndicator } from 'react-icons/md'
import slugify from 'slugify'
import {
  AudiostreamSourceWithProfile,
  ImageUploadUrl,
  UpdateAudiostreamInput,
} from 'types/graphql'

import {
  Controller,
  FieldArrayWithId,
  FieldError,
  Form,
  FormProvider,
  Label,
  RWGqlError,
  Submit,
  TextField,
  useFieldArray,
  useForm,
  useFormContext,
} from '@cedarjs/forms'
import { useMutation } from '@cedarjs/web'
import { toast } from '@cedarjs/web/toast'

import { DeleteIcon } from 'src/assets/DeleteIcon'
import { CommonArrayItem } from 'src/common/types'
import Dropdown from 'src/components/Custom/Dropdown/Dropdown'
import HeadingLevel from 'src/components/Custom/Heading/Heading'
import { AudiostreamProfileLabels } from 'src/lib/enumLabels'
import {
  CREATE_IMAGE_UPLOAD_URL_MUTATION,
  createUploadedImagePreviewUrl,
  transformImage,
  uploadImageToCloudflare,
} from 'src/lib/images'
import { slugRule, urlRule } from 'src/lib/validationRules'
import YesNoConfirmationModal from 'src/pages/HeroLineupPage/HeroLineup/YesNoConfirmationModal'

import Button from '../../../components/Custom/Button/Button'
import DragAndDropImageArea from '../../../components/Custom/DragAndDropImageArea/DragAndDropImageArea'
import Loader from '../../../components/Custom/Loader/Loader'
import CustomModal from '../../../components/Custom/Modal/CustomModal'

import { Audiostream } from './Audiostream.tsx'

const LOGO_PREVIEW_SIZE = {
  height: 100,
  width: 200,
}

interface ConfigureAudiostreamModalProps {
  editingItem: Audiostream
  loading: boolean
  stationId: string
  streamScheduleOptions: CommonArrayItem[]
  showModal: boolean
  error?: RWGqlError
  handleModalClose: () => void
  onSave: (
    inputData: UpdateAudiostreamInput,
    id?: Audiostream['id']
  ) => Promise<void>
  onDelete?: (item: Audiostream) => void
}

const ConfigureAudiostreamModal = ({
  editingItem,
  loading = false,
  stationId,
  streamScheduleOptions,
  showModal,
  error,
  handleModalClose,
  onSave,
  onDelete,
}: ConfigureAudiostreamModalProps) => {
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(editingItem?.logo)
  const [saved, setSaved] = useState(false)

  const [isImageUploading, setIsImageUploading] = useState(false)

  const formMethods = useForm<Audiostream>({
    defaultValues: {
      logo: editingItem?.logo,
      name: editingItem?.name,
      sources: editingItem?.sources || [],
      streamScheduleId: editingItem?.streamScheduleId ?? '',
    },
  })

  const {
    control,
    formState: { isDirty },
  } = formMethods

  const closeModal = useCallback(() => {
    if (isDirty) {
      if (
        window.confirm(
          'Are you sure you want to leave? You may lose unsaved changes.'
        )
      ) {
        handleModalClose()
      }
    } else {
      handleModalClose()
    }
  }, [isDirty, handleModalClose])

  // Close the modal once the form has been saved. UseEffect ensures
  // the isDirty state is set before handleModalClose is called so that
  // the confirm message is displayed correctly.
  useEffect(() => {
    if (saved) {
      handleModalClose()
    }
  }, [handleModalClose, saved])

  const onSubmit = async (data: Audiostream) => {
    if (isImageUploading) return
    if (!isDirty) {
      handleModalClose()
      return
    }

    // Adjust sources weights based on their order in the form.
    const sources = data.sources.map((source, index) => ({
      profile: source.profile,
      url: source.url,
      weight: index,
    }))

    const payload: UpdateAudiostreamInput = {
      name: data.name,
      logo: data.logo,
      slug: data.slug || slugify(data.name, { lower: true, strict: true }),
      sources: sources,
      stationId,
      streamScheduleId: data.streamScheduleId || null,
    }

    onSave(payload, editingItem?.id).then(() => setSaved(true))
  }

  const [createImageUploadUrl] = useMutation<{
    createImageUploadUrl: ImageUploadUrl
  }>(CREATE_IMAGE_UPLOAD_URL_MUTATION)

  const handleUpload = async (files: File[]) => {
    const uploadImage = async () => {
      const mutationResult = await createImageUploadUrl()
      const { uploadUrl } = mutationResult.data.createImageUploadUrl
      return uploadImageToCloudflare(uploadUrl, files[0])
    }

    setIsImageUploading(true)
    let imageUrl: string
    try {
      imageUrl = await toast.promise(uploadImage(), {
        loading: 'Uploading image...',
        success: 'Image uploaded successfully',
        error: 'Failed to upload image',
      })
    } catch {
      formMethods.setError('logo', {
        type: 'manual',
        message: 'Failed to upload image',
      })
      return
    } finally {
      setIsImageUploading(false)
    }
    if (imageUrl) {
      formMethods.setValue('logo', imageUrl, { shouldDirty: true })
      formMethods.clearErrors('logo')
      setLogoPreviewUrl(
        createUploadedImagePreviewUrl({
          uploadedImageUrl: imageUrl,
          width: 200,
        })
      )
    } else {
      formMethods.setError('logo', {
        type: 'manual',
        message: 'Failed to upload image',
      })
    }
  }

  const isValidUrl = (input: string) => {
    const urlPattern = /^http/
    if (input && urlPattern.test(input)) {
      return true
    }
    return 'Logo URL is required.'
  }
  return (
    <CustomModal
      showModal={showModal}
      setShowModal={closeModal}
      modalWidth="max-w-3xl relative"
    >
      <HeadingLevel level={1} levelStyle={2}>
        {`${editingItem ? 'Edit' : 'Create'} audio stream`}
      </HeadingLevel>
      <FormProvider {...formMethods}>
        <Form formMethods={formMethods} onSubmit={onSubmit} error={error}>
          <Label
            name="name"
            className="mt-2 block text-sm font-medium text-gray-700"
          >
            Name
          </Label>
          <TextField
            className="my-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            name="name"
            defaultValue={editingItem?.name}
            validation={{ required: 'Please provide a channel name' }}
          />
          <FieldError className="text-red-700" name="name" />

          <Label
            name="streamScheduleId"
            className="mt-4 block text-sm font-medium text-gray-700"
          >
            Stream schedule
          </Label>
          <Dropdown
            buttonText="Select stream schedule"
            fullWidth={true}
            onOptionsChange={({ value }) =>
              formMethods.setValue('streamScheduleId', value, {
                shouldDirty: true,
              })
            }
            options={streamScheduleOptions}
            value={formMethods.watch('streamScheduleId') ?? ''}
          />

          {editingItem?.slug && (
            <>
              <Label
                name="slug"
                className="mt-4 block text-sm font-medium text-gray-700"
              >
                Slug
              </Label>
              <TextField
                className="my-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                name="slug"
                defaultValue={editingItem.slug}
                validation={{
                  ...slugRule,
                  required:
                    'Please provide a slug, using lowercase alphanumeric characters and hyphens only, no leading/trailing hyphens',
                }}
              />
              <FieldError className="text-red-700" name="slug" />
            </>
          )}

          <div className="my-6 flex flex-col">
            <Label
              name="Logo"
              className="block text-sm font-medium text-gray-700"
            />
            <div className="flex flex-col space-y-2 lg:flex-row lg:space-x-4">
              <div className="mb-2 w-3/4 lg:w-1/2">
                <div className="mt-6">
                  {logoPreviewUrl ? (
                    <img
                      height={LOGO_PREVIEW_SIZE.height}
                      width={LOGO_PREVIEW_SIZE.width}
                      src={transformImage({
                        imageUrl: logoPreviewUrl,
                        options: {
                          height: LOGO_PREVIEW_SIZE.height * 2,
                          width: LOGO_PREVIEW_SIZE.width * 2,
                        },
                      })}
                      alt="logo"
                    />
                  ) : (
                    <em>Not set</em>
                  )}
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <Controller
                  control={control}
                  name="logo"
                  rules={{
                    required:
                      'Logo is required (click "Upload File" after selecting a logo file)',
                    validate: isValidUrl,
                  }}
                  render={() => (
                    <DragAndDropImageArea
                      id="channelLogo"
                      onUpload={handleUpload}
                      previewHeight={LOGO_PREVIEW_SIZE.height}
                      previewWidth={LOGO_PREVIEW_SIZE.width}
                      isDisabled={isImageUploading || loading}
                      errorClassName={
                        formMethods.formState.errors.logo?.message
                          ? 'error'
                          : undefined
                      }
                    />
                  )}
                />
              </div>
            </div>
            <FieldError className="text-red-700" name="logo" />
          </div>

          <div className="mb-2">
            <AudiostreamSourceFields />
          </div>

          {/* Server error messages */}
          {error?.message && (
            <div className="rw-form-error-list mt-5 rounded-md bg-red-50 p-4">
              <p className="rw-form-error-title text-sm font-medium text-red-800">
                {error.message}
              </p>
            </div>
          )}

          <div className="mt-10 flex w-full items-center justify-between">
            {editingItem && (
              <Button
                title="Delete"
                icon={<MdDeleteForever size="20px" />}
                outlineColor="bg-red-600 text-white hover:bg-red-700"
                onClick={() => {
                  if (editingItem?.id) onDelete(editingItem)
                }}
                isDisabled={loading}
              />
            )}
            <div className="ml-auto flex gap-4">
              <Submit
                className="relative inline-flex h-10 items-center justify-center overflow-hidden rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600"
                disabled={loading}
              >
                {loading || isImageUploading ? <Loader /> : 'Save'}
              </Submit>
              <Button
                title="Cancel"
                outlineColor="border-red-600 text-red-600 hover:border-red-700 hover:text-red-700"
                onClick={closeModal}
                isDisabled={loading}
              />
            </div>
          </div>
        </Form>
      </FormProvider>
    </CustomModal>
  )
}
export default ConfigureAudiostreamModal

type AudiostreamSourceField = {
  sources: Array<AudiostreamSourceWithProfile>
}

const AudiostreamSourceFields = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [deletingSourceIndex, setDeletingSourceIndex] = useState(null)
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<Audiostream>()
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: 'sources',
    rules: {
      required: {
        value: true,
        message: 'Please provide at least one source',
      },
    },
  })

  const currentSources = getValues('sources')

  const handleAddNewSource = () => {
    append({ profile: null, url: '', weight: currentSources.length })
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (active.id !== over.id) {
      const oldIndex = fields.findIndex((item) => item.id === active.id)
      const newIndex = fields.findIndex((item) => item.id === over.id)
      move(oldIndex, newIndex)

      const newFields = arrayMove(fields, oldIndex, newIndex)
      newFields.forEach((_field, index) => {
        setValue(`sources.${index}.weight`, index, { shouldDirty: true })
      })
    }
  }

  return (
    <div className="rounded-lg border bg-white p-6">
      <Label name="sources" className="text-sm font-medium text-gray-700">
        Sources
      </Label>
      <div className="my-4 flex items-center justify-end space-x-2">
        <div className="flex flex-1 flex-row items-center">
          <IoHandLeftOutline className="mr-2" />
          <p className="text-sm font-normal text-gray-900">
            Drag and drop to reorder.
          </p>
        </div>
        <div className="flex flex-col">
          <Button title="Add source" onClick={() => handleAddNewSource()} />
        </div>
      </div>

      <ul className="flex flex-col gap-3">
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={fields}
            strategy={verticalListSortingStrategy}
          >
            {fields.map((field, index) => {
              return (
                <SortableAudiostreamSourceField
                  key={field.id}
                  field={field}
                  index={index}
                  onDelete={() => {
                    setDeletingSourceIndex(index)
                    setShowConfirmModal(true)
                  }}
                />
              )
            })}
          </SortableContext>
        </DndContext>
      </ul>
      {errors.sources?.root?.message && (
        <span className="text-sm text-red-700">
          {errors.sources.root.message}
        </span>
      )}

      <YesNoConfirmationModal
        content={'Are you sure you wish to delete this audio stream source?'}
        show={showConfirmModal}
        setShowModal={setShowConfirmModal}
        onConfirm={() => {
          if (deletingSourceIndex !== null) remove(deletingSourceIndex)
          setShowConfirmModal(false)
        }}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  )
}

type SortableAudiostreamSourceFieldProps = {
  field: FieldArrayWithId<AudiostreamSourceField>
  index: number
  onDelete: (index: number) => void
}
const SortableAudiostreamSourceField = ({
  field,
  index,
  onDelete,
}: SortableAudiostreamSourceFieldProps) => {
  const { control, register } = useFormContext()
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: field.id,
    })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  }

  const profileOptions = Array.from(AudiostreamProfileLabels).map(
    ([value, label]) => ({
      value,
      label,
    })
  )

  return (
    <li
      key={field.id}
      ref={setNodeRef}
      className="block rounded-lg bg-white"
      style={style}
    >
      <div className="align-items flex gap-2">
        <div className="flex items-center">
          <MdDragIndicator
            size={20}
            className="text-gray-500"
            {...listeners}
            {...attributes}
          />
        </div>
        <div className="flex-1">
          <TextField
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            validation={{
              ...urlRule,
              required: 'Please provide a source url',
            }}
            {...register(`sources.${index}.url` as const)}
          />
          <FieldError
            className="text-sm text-red-700"
            name={`sources.${index}.url`}
          />
        </div>

        <div className="min-w-48">
          <Controller
            control={control}
            rules={{ required: 'Please select a profile' }}
            render={({ field }) => (
              <Dropdown
                options={profileOptions}
                onOptionsChange={({ value }) => {
                  field.onChange(value)
                }}
                buttonText="select profile"
                menuListClass="text-right"
                fullWidth={true}
                {...field}
              />
            )}
            {...register(`sources.${index}.profile` as const)}
          />
          <FieldError
            className="block text-sm text-red-700"
            name={`sources.${index}.profile`}
          />
        </div>

        <div className="flex items-center">
          <button
            type="button"
            className="rw-button rw-button-small rw-button-red"
            title={`Delete source`}
            onClick={() => {
              onDelete(index)
            }}
          >
            <DeleteIcon classStyle="fill-red" />
          </button>
        </div>
      </div>
    </li>
  )
}
